import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, X, FileText, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status pill colour map. Mirrors backend INVOICE_STATUSES.
// "draft" = blue, "sent_*" / "po_received" / "sent_to_accounts" = green,
// "paid" = teal-ish green, "void" = grey.
const STATUS_STYLES = {
  draft:               { bg: '#E6F1FB', fg: '#0C447C', label: 'Draft' },
  sent_to_housekeeper: { bg: '#FAEEDA', fg: '#854F0B', label: 'Awaiting PO' },
  po_received:         { bg: '#FAEEDA', fg: '#854F0B', label: 'PO received' },
  sent_to_accounts:    { bg: '#EAF3DE', fg: '#3B6D11', label: 'Sent' },
  paid:                { bg: '#E1F5EE', fg: '#0F6E56', label: 'Paid' },
  void:                { bg: '#F1EFE8', fg: '#5F5E5A', label: 'Voided' },
};

// Compute the most recent Sunday in YYYY-MM-DD form (used as week-ending default).
function lastSundayIso() {
  const d = new Date();
  // getDay(): 0=Sun, 1=Mon, ..., 6=Sat. We want the most recent Sunday strictly
  // in the past (or today if today is Sunday).
  const dow = d.getDay();
  const daysBack = dow === 0 ? 0 : dow;
  d.setDate(d.getDate() - daysBack);
  return d.toISOString().slice(0, 10);
}

const StatusPill = ({ status }) => {
  const s = STATUS_STYLES[status] || { bg: '#F1EFE8', fg: '#5F5E5A', label: status };
  return (
    <span style={{ background: s.bg, color: s.fg }}
      className="px-3 py-1 rounded-full text-xs font-medium">
      {s.label}
    </span>
  );
};

const Invoicing = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // New Invoice modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState('');
  const [newWeekEnding, setNewWeekEnding] = useState(lastSundayIso());
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    Promise.all([fetchInvoices(), fetchCustomers()]).then(() => setLoading(false));
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices`, { credentials: 'include' });
      const data = await res.json();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error fetching invoices:', e);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/customers`, { credentials: 'include' });
      const data = await res.json();
      // Only customers with location_ref + customer_ref are invoiceable.
      const eligible = (data || []).filter(c => c.location_ref != null && c.customer_ref != null && c.active !== false);
      setCustomers(eligible);
    } catch (e) {
      console.error('Error fetching customers:', e);
    }
  };

  // ---------------- New Invoice modal ----------------

  const openNewModal = () => {
    setNewCustomerId('');
    setNewWeekEnding(lastSundayIso());
    setCreateError('');
    setShowNewModal(true);
  };

  const closeNewModal = () => {
    if (creating) return;
    setShowNewModal(false);
  };

  // Preview opens the PDF preview in a new tab — no draft saved yet.
  const handlePreview = async () => {
    if (!newCustomerId) {
      setCreateError('Pick a customer first.');
      return;
    }
    setCreateError('');
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/pdf-preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: Number(newCustomerId),
          week_ending_date: newWeekEnding,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Preview failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Don't revoke immediately — the new tab needs the URL alive for at least
      // a moment to load the inline PDF. 60s is generous.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Preview error:', e);
      setCreateError(e.message || 'Preview failed');
    }
  };

  // Create draft persists the invoice and navigates to its editor.
  const handleCreateDraft = async () => {
    if (!newCustomerId) {
      setCreateError('Pick a customer first.');
      return;
    }
    setCreateError('');
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: Number(newCustomerId),
          week_ending_date: newWeekEnding,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Create failed (${res.status})`);
      }
      const inv = await res.json();
      setShowNewModal(false);
      navigate(`/operations/invoicing/${inv.id}`);
    } catch (e) {
      console.error('Create draft error:', e);
      setCreateError(e.message || 'Create draft failed');
    } finally {
      setCreating(false);
    }
  };

  // ---------------- Filtering ----------------

  const filtered = invoices.filter(inv => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
    if (dateFrom && inv.week_ending_date < dateFrom) return false;
    if (dateTo && inv.week_ending_date > dateTo) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const hay = `${inv.invoice_number || ''} ${inv.customer_name || ''} ${inv.customer_code || ''} ${inv.po_number || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const hasActiveFilters = statusFilter !== 'all' || dateFrom || dateTo;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-gray-600">Loading invoices...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>
          Invoicing
        </h1>
        <button
          onClick={openNewModal}
          className="flex items-center px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] transition-colors">
          <Plus size={20} className="mr-2" /> New Invoice
        </button>
      </div>

      {/* Search + filter bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by invoice number, customer, or PO..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F2D3D] focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
              hasActiveFilters ? 'bg-[#1F2D3D] text-white border-[#1F2D3D]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}>
            <Filter size={18} className="mr-2" /> Filters
            {hasActiveFilters && <span className="ml-2 bg-white text-[#1F2D3D] rounded-full px-2 py-0.5 text-xs font-bold">Active</span>}
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F2D3D]">
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="sent_to_housekeeper">Awaiting PO</option>
                <option value="po_received">PO received</option>
                <option value="sent_to_accounts">Sent</option>
                <option value="paid">Paid</option>
                <option value="void">Voided</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Week ending from</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F2D3D]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Week ending to</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F2D3D]"
              />
            </div>
            {hasActiveFilters && (
              <div className="md:col-span-3">
                <button
                  onClick={() => { setStatusFilter('all'); setDateFrom(''); setDateTo(''); }}
                  className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800">
                  <X size={16} className="mr-1" /> Clear filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-4 text-sm text-gray-600">
        Showing {filtered.length} of {invoices.length} invoice{invoices.length === 1 ? '' : 's'}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No invoices found</p>
            {invoices.length === 0 && (
              <p className="text-sm text-gray-400">Click + New Invoice to create your first one.</p>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Number</th>
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Customer</th>
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Week ending</th>
                <th className="text-right py-3 px-4 text-gray-600 font-medium">Total inc VAT</th>
                <th className="text-left py-3 px-4 text-gray-600 font-medium">Status</th>
                <th className="text-right py-3 px-4 text-gray-600 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id} className="border-t hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono text-sm">{inv.invoice_number || '—'}</td>
                  <td className="py-3 px-4">
                    {inv.customer_name || `Customer ${inv.customer_id}`}
                    {inv.customer_code && (
                      <span className="text-xs text-gray-400 ml-2">{inv.customer_code}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm">{inv.week_ending_date}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">£{Number(inv.sub_total_inc_vat || 0).toFixed(2)}</td>
                  <td className="py-3 px-4"><StatusPill status={inv.status} /></td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => navigate(`/operations/invoicing/${inv.id}`)}
                      className="inline-flex items-center text-[#1F2D3D] hover:underline text-sm">
                      <Eye size={16} className="mr-1" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New Invoice modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={closeNewModal}>
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>
                New invoice
              </h2>
              <button onClick={closeNewModal} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <select
                value={newCustomerId}
                onChange={(e) => setNewCustomerId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F2D3D]">
                <option value="">— Select a customer —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                ))}
              </select>
            </div>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Week ending</label>
              <input
                type="date"
                value={newWeekEnding}
                onChange={(e) => setNewWeekEnding(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F2D3D]"
              />
              <p className="text-xs text-gray-400 mt-1">Defaults to last Sunday.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 my-4">
              Click <em>Preview</em> to see the proposed PDF in a new tab — nothing is saved
              until you click <em>Create draft</em>.
            </div>

            {createError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg p-3 mb-3">{createError}</div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={closeNewModal}
                disabled={creating}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={creating}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Preview
              </button>
              <button
                onClick={handleCreateDraft}
                disabled={creating}
                className="px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-60">
                {creating ? 'Creating...' : 'Create draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoicing;
